
import React, { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    firm: '',
    phone: '',
    practice_area: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d5nciofpl7huldvcis9g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          firm: '',
          phone: '',
          practice_area: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <div className="flex items-center mb-4">
              <i className="ri-mail-line text-[#e10708] text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-[#e10708] text-sm uppercase tracking-widest font-light">
                Get In Touch
              </span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-serif text-[#1A1F2E] leading-tight mb-6">
              Start Your<br />
              Confidential Assessment
            </h2>

            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Schedule a no-obligation technology assessment. We'll review your
              current infrastructure, identify vulnerabilities, and provide a
              roadmap for protecting your practice.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#e10708]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-[#e10708] text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-[#1A1F2E] font-bold mb-1">Phone</h3>
                  <p className="text-gray-600">+1 (855) 283-7848</p>
                  <p className="text-sm text-gray-500">Mon-Fri 8am-5pm CST</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#e10708]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-[#e10708] text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-[#1A1F2E] font-bold mb-1">Email</h3>
                  <p className="text-gray-600">legal@avertnetworkservices.com</p>
                  <p className="text-sm text-gray-500">24-hour response guarantee</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#e10708]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-[#e10708] text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-[#1A1F2E] font-bold mb-1">Office</h3>
                  <p className="text-gray-600">Irving, TX</p>
                  <p className="text-gray-600">San Antonio, TX</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF9F6] rounded-xl p-6 border border-[#e10708]/20">
              <div className="flex items-start">
                <i className="ri-shield-check-line text-[#e10708] text-2xl mr-3 mt-1 w-6 h-6 flex items-center justify-center"></i>
                <div>
                  <h4 className="text-[#1A1F2E] font-bold mb-2">
                    Complete Confidentiality
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    All consultations are protected under NDA. We understand the
                    sensitive nature of law firm operations and maintain the
                    highest standards of discretion.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10 border border-gray-200">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="mb-6">
                <label htmlFor="name" className="block text-[#1A1F2E] font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-[#1A1F2E] font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm"
                  placeholder="john.smith@lawfirm.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="firm" className="block text-[#1A1F2E] font-semibold mb-2">
                  Law Firm Name *
                </label>
                <input
                  type="text"
                  id="firm"
                  name="firm"
                  value={formData.firm}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm"
                  placeholder="Smith & Associates LLP"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="phone" className="block text-[#1A1F2E] font-semibold mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="practice_area" className="block text-[#1A1F2E] font-semibold mb-2">
                  Primary Practice Area
                </label>
                <select
                  id="practice_area"
                  name="practice_area"
                  value={formData.practice_area}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select practice area</option>
                  <option value="M&A">Mergers &amp; Acquisitions</option>
                  <option value="Litigation">Litigation</option>
                  <option value="Personal Injury">Personal Injury</option>
                  <option value="Corporate">Corporate Law</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Family Law">Family Law</option>
                  <option value="Criminal Defense">Criminal Defense</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-[#1A1F2E] font-semibold mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your technology needs and concerns..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <p className="text-green-800 text-sm font-medium">
                      Thank you! We'll contact you within 24 hours.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <p className="text-red-800 text-sm font-medium">
                      Something went wrong. Please try again or call us directly.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#e10708] text-white px-8 py-4 rounded-lg text-base font-bold hover:bg-[#c10607] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Send Message
                    <i className="ri-send-plane-fill ml-2 w-5 h-5 flex items-center justify-center"></i>
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
